'use client';

import { createContext, useContext, useMemo, useRef } from 'react';

/**
 * Tapestry stagger queue — ensures only one element draws in at a time.
 *
 * Callers register with `schedule(durationMs, onFire)`: the queue calls
 * `onFire()` when it's their turn. `durationMs` is how long their draw-in
 * is expected to take; the queue advances automatically after that. The
 * returned handle exposes `cancel()` which works in both phases:
 *
 *   - Before fire: removes the item from the queue. Items behind it
 *     don't fire any earlier — they are already at the head once the
 *     active slot finishes — but the cancelled item leaves no gap.
 *   - After fire: marks the active slot done, immediately advances the
 *     queue. This is the critical case: if an element's draw aborts
 *     mid-flight (e.g. its layout deps change and the effect re-runs),
 *     we want the queue to move on rather than hold the slot until its
 *     original endsAt — that's what produced "awkward long pauses."
 */

const StaggerContext = createContext(null);

export function TapestryStaggerProvider({ children }) {
  const stateRef = useRef({
    queue: [],
    active: null,
    activeTimer: null,
  });

  const api = useMemo(() => {
    const state = stateRef.current;

    const pump = () => {
      if (state.active) return;
      const next = state.queue.shift();
      if (!next) return;
      state.active = next;
      next.fired = true;
      try {
        next.onFire?.();
      } catch {
        // onFire failures shouldn't lock up the queue
      }
      state.activeTimer = setTimeout(() => {
        if (state.active !== next) return;
        state.active = null;
        state.activeTimer = null;
        pump();
      }, next.durationMs);
    };

    return {
      schedule(durationMs, onFire) {
        const item = { durationMs, onFire, fired: false };
        state.queue.push(item);
        pump();
        return {
          cancel: () => {
            if (item.fired) {
              // Active slot — advance the queue now rather than waiting
              // out the original duration.
              if (state.active === item) {
                if (state.activeTimer) clearTimeout(state.activeTimer);
                state.activeTimer = null;
                state.active = null;
                pump();
              }
            } else {
              const idx = state.queue.indexOf(item);
              if (idx >= 0) state.queue.splice(idx, 1);
            }
          },
        };
      },
    };
  }, []);

  return <StaggerContext.Provider value={api}>{children}</StaggerContext.Provider>;
}

export function useTapestryStagger() {
  return useContext(StaggerContext);
}
